import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { formatCurrency } from "@/lib/money";
import { canWriteGuestPitchBooking } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import { Table } from "@/components/ui/Table";
import { RegisterVisitorModal } from "@/pages/visitors/RegisterVisitorModal";
import { visitorPaymentModeLabel } from "@/lib/visitorRegistration";
import type { VisitorRegistration } from "@/lib/visitorRegistration";

export function VisitorsPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteGuestPitchBooking(groups);
  const [registrations, setRegistrations] = useState<VisitorRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<VisitorRegistration[]>("/api/v1/visitor-registrations")
      .then(setRegistrations)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPageMeta({
      title: "Besucher",
      subtitle: "Anmeldungen für Dauercamper",
    });
    load();
    return () => setPageMeta(null);
  }, [load, setPageMeta]);

  return (
    <div className="space-y-6">
      <p className="text-sm text-label-secondary">
        Melden Sie Besucher für Dauercamper an. Die Gebühren werden über die Buchungszuordnung aus
        den hinterlegten Artikeln berechnet. Sie können die Beträge sofort kassieren oder auf die
        Jahresendabrechnung des Vertrags buchen.
      </p>

      <RegisterVisitorModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
      />

      <div className="flex justify-end">
        {canEdit ? (
          <Button type="button" className="w-full sm:w-auto" onClick={() => setCreateOpen(true)}>
            Besucher anmelden
          </Button>
        ) : null}
      </div>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Laden…</p>
      ) : (
        <Table wide emptyMessage="Noch keine Besucheranmeldungen." isEmpty={registrations.length === 0}>
          <thead>
            <tr>
              <th>Datum</th>
              <th>Dauercamper</th>
              <th>Platz</th>
              <th>Erw.</th>
              <th>Kinder</th>
              <th>Autos</th>
              <th>Betrag</th>
              <th>Zahlung</th>
              <th>Rechnung</th>
            </tr>
          </thead>
          <tbody>
            {registrations.map((r) => (
              <ClickableTableRow key={r.id} ariaLabel="Besucheranmeldung" onActivate={() => undefined}>
                <td className="tabular-nums">{formatIsoDateShort(r.visit_date)}</td>
                <td className="font-medium">{r.guest_name ?? "—"}</td>
                <td>{r.pitch_code ?? "—"}</td>
                <td className="tabular-nums">{r.adults}</td>
                <td className="tabular-nums">{r.children}</td>
                <td className="tabular-nums">{r.extra_cars}</td>
                <td className="tabular-nums">{formatCurrency(r.total_amount)}</td>
                <td>{visitorPaymentModeLabel(r.payment_mode)}</td>
                <td>{r.invoice_number ?? "—"}</td>
              </ClickableTableRow>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
